import {
  Button,
  Checkbox,
  Flex,
  FormLabel,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  VStack,
  InputGroup,
} from '@chakra-ui/react';
import { Formik, Form, getIn } from 'formik';
import _ from 'lodash';
import React from 'react';

import {
  error as commonError,
  finleyAssert,
  utils as commonUtils,
} from '@finley-technologies/common';
import {
  CreditAgreement,
  Deliverable,
} from '@finley-technologies/finley-types';

import { WipConfig } from '../../types';
import { FormFieldLabel } from 'packages/common-frontend/components/FormField';
import { Select } from 'packages/common-frontend/components/Select';

const createDeliverablesPermissionShell = ({
  entity,
  permissionTypes = [],
}: {
  entity: CreditAgreement.DomainEntity.Items.BaseOrganizationEntity['entity'];
  permissionTypes?: Deliverable.v3DEPRECATED.PermissionType[];
}) => {
  return {
    entity,
    permissionTypes,
  };
};

type EditReportingDeliverablesPermissionsModalProps = {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  wipConfig: WipConfig;
  partyPermissions: CreditAgreement.Config.DeliverablePropertyDEPRECATED.PartyPermissionWithEntity[];
  deliverableName: string;
  deliverablePermission?: CreditAgreement.Config.DeliverablePropertyDEPRECATED.PartyPermissionWithEntity;
  onSubmit: (props: any) => void;
};
export const EditReportingDeliverablesPermissionsModal = ({
  deliverableName,
  partyPermissions,
  isOpen,
  setIsOpen,
  deliverablePermission,
  wipConfig: { formProps, organizationMap },
  onSubmit,
}: EditReportingDeliverablesPermissionsModalProps) => {
  // create party options from parties configured in facility mechanics
  const parties = getIn(formProps.values, 'facilityMechanics.parties');
  const finley = _.find(
    organizationMap,
    ({ entity: { slug } }) => slug === 'finley',
  );

  finleyAssert(
    finley,
    new commonError.FinleyError('Finley not found in organizationMap', {
      metadata: { logMeta: { parties } },
    }),
  );

  const uniqueParties: CreditAgreement.DomainEntity.Items.BaseOrganizationEntity['entity'][] = _.reduce(
    parties,
    (
      acc: CreditAgreement.DomainEntity.Items.BaseOrganizationEntity['entity'][],
      entities: CreditAgreement.DomainEntity.Items.BaseOrganizationEntity[],
    ) => {
      if (_.isEmpty(entities)) return acc;
      const newParties = _.compact(
        _.map(entities, ({ entity }) =>
          _.includes(_.map(acc, 'id'), entity.id) ? null : entity,
        ),
      );

      const newOptions = [...acc, ...newParties];
      return newOptions;
    },
    [finley.entity],
  );

  const currentPermission: CreditAgreement.Config.DeliverablePropertyDEPRECATED.PartyPermissionWithEntity =
    deliverablePermission ||
    createDeliverablesPermissionShell({
      entity: finley.entity,
    });

  const currentPermissionOrganizations: string[] = _.map(
    partyPermissions,
    ({ entity: { id } }) => id,
  );

  const generateNewPermissions = (
    submittedDeliverablePermission: CreditAgreement.Config.DeliverablePropertyDEPRECATED.PartyPermissionWithEntity,
  ) => {
    const permissionIndex = _.findIndex(
      partyPermissions,
      ({ entity: { id } }) => id === submittedDeliverablePermission.entity.id,
    );
    if (permissionIndex !== -1) {
      const newPermissions = _.cloneDeep(partyPermissions);
      newPermissions[permissionIndex] = submittedDeliverablePermission;
      return newPermissions;
    }
    return [...partyPermissions, submittedDeliverablePermission];
  };

  return (
    <Modal isOpen={isOpen} onClose={() => setIsOpen(false)} size="2xl">
      <ModalOverlay />
      <ModalContent padding="20px">
        <ModalHeader>
          {`${
            deliverablePermission
              ? `Edit ${deliverableName}`
              : `Add ${deliverableName}`
          } Permission`}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Flex flexDirection="row" alignItems="flex-start" mb={5}>
            <FormLabel w="60" mb="0">
              Property
            </FormLabel>
          </Flex>
          <Formik
            onSubmit={(props) => {
              // set to change setFieldValue key
              const newPermissions = generateNewPermissions(props);
              onSubmit(newPermissions);
              setIsOpen(false);
            }}
            initialValues={{
              ...currentPermission,
            }}
          >
            {(localFormProps) => {
              const validPartyOptions = _.compact(
                _.map(uniqueParties, ({ id, slug }) =>
                  _.includes(currentPermissionOrganizations, id) &&
                  id !== currentPermission.entity.id
                    ? null
                    : { label: commonUtils.toTitleCase(slug), value: id },
                ),
              );
              return (
                <Form onSubmit={localFormProps.handleSubmit}>
                  <VStack spacing={5}>
                    <InputGroup w="100%">
                      <FormFieldLabel labelWidth="60" label="Party" />
                      <Select
                        isDisabled={false}
                        value={localFormProps.values.entity.id}
                        onChange={(value) => {
                          if (value) {
                            const { entity } = organizationMap[value];
                            finleyAssert(
                              entity,
                              new commonError.FinleyError(
                                `Organization not found for id ${value}]`,
                                {
                                  metadata: { logMeta: { parties } },
                                },
                              ),
                            );
                            localFormProps.setFieldValue('entity', entity);
                          }
                        }}
                        options={validPartyOptions}
                        width="sm"
                      />
                    </InputGroup>

                    <InputGroup width="100%">
                      <FormFieldLabel label="View" labelWidth="60" />
                      <Checkbox
                        type="checkbox"
                        data-testid="view-checkbox"
                        name="view"
                        onChange={(clickEvent) => {
                          return clickEvent.target.checked
                            ? localFormProps.setFieldValue(
                                'permissionTypes',
                                _.uniq([
                                  ...localFormProps.values.permissionTypes,
                                  'view',
                                ]),
                              )
                            : localFormProps.setFieldValue(
                                'permissionTypes',
                                _.without(
                                  localFormProps.values.permissionTypes,
                                  'view',
                                ),
                              );
                        }}
                        checked={_.includes(
                          localFormProps.values.permissionTypes,
                          'view',
                        )}
                        w="sm"
                        defaultChecked={_.includes(
                          localFormProps.values.permissionTypes,
                          'view',
                        )}
                      />
                    </InputGroup>
                    <InputGroup w="100%">
                      <FormFieldLabel label="Download" labelWidth="60" />
                      <Checkbox
                        type="checkbox"
                        data-testid="download-checkbox"
                        name="download"
                        onChange={(clickEvent) => {
                          return clickEvent.target.checked
                            ? localFormProps.setFieldValue(
                                'permissionTypes',
                                _.uniq([
                                  ...localFormProps.values.permissionTypes,
                                  'download',
                                ]),
                              )
                            : localFormProps.setFieldValue(
                                'permissionTypes',
                                _.without(
                                  localFormProps.values.permissionTypes,
                                  'download',
                                ),
                              );
                        }}
                        checked={_.includes(
                          localFormProps.values.permissionTypes,
                          'download',
                        )}
                        w="sm"
                        defaultChecked={_.includes(
                          localFormProps.values.permissionTypes,
                          'download',
                        )}
                      />
                    </InputGroup>
                  </VStack>
                  <Flex justifyContent="center" mt="8">
                    <Button
                      type="submit"
                      aria-label="Done"
                      isDisabled={_.keys(formProps.errors).length > 0}
                    >
                      Done
                    </Button>
                  </Flex>
                </Form>
              );
            }}
          </Formik>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
